import { useState, useEffect } from 'react';
import { Search, Phone, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useActiveQueues } from '@/lib/hooks/useQueues';
import { useTheme } from '@/contexts/ThemeContext';

interface MakeCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  onMakeCall: (number: string, callSource: CallSource) => void;
  agentId: string | null;
}

interface CallSource {
  type: 'queue' | 'direct' | 'outbound_list';
  id?: string;
  name: string;
}

interface Customer {
  id: string;
  customer_name: string;
  customer_phone: string;
  customer_address: string;
  company: 'abraham' | 'hales' | 'climate';
}

interface OutboundList {
  id: string;
  name: string;
  company_name: string;
}

export function MakeCallModal({ isOpen, onClose, onMakeCall, agentId }: MakeCallModalProps) {
  const { theme } = useTheme();
  const { data: queues } = useActiveQueues({ enabled: isOpen });
  const [callSource, setCallSource] = useState<CallSource>({ type: 'direct', name: 'Direct Line' });
  const [phoneNumber, setPhoneNumber] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<'abraham' | 'hales' | 'climate'>('climate');
  const [searchResults, setSearchResults] = useState<Customer[]>([]);
  const [searching, setSearching] = useState(false);
  const [outboundLists, setOutboundLists] = useState<OutboundList[]>([]);
  const [activeTab, setActiveTab] = useState<'dialpad' | 'search'>('dialpad');

  // Load outbound lists
  useEffect(() => {
    if (isOpen) {
      loadOutboundLists();
    }
  }, [isOpen]);

  const loadOutboundLists = async () => {
    const { data } = await supabase
      .from('outbound_calling_lists')
      .select('id, name, company_name')
      .eq('is_active', true)
      .order('name');
    
    if (data) {
      setOutboundLists(data);
    }
  };

  // Search customers
  useEffect(() => {
    if (searchQuery.length >= 2 && activeTab === 'search') {
      searchCustomers();
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, selectedCompany, activeTab]);

  const searchCustomers = async () => {
    setSearching(true);
    try {
      const tableName = `${selectedCompany}_customers`;
      const { data } = await supabase
        .from(tableName)
        .select('id, customer_name, customer_phone, customer_address')
        .or(`customer_name.ilike.%${searchQuery}%,customer_address.ilike.%${searchQuery}%`)
        .limit(10);

      if (data) {
        setSearchResults(data.map(c => ({ ...c, company: selectedCompany })));
      }
    } catch (error) {
      console.error('Error searching customers:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleDialPadClick = (digit: string) => {
    setPhoneNumber(prev => prev + digit);
  };

  const handleBackspace = () => {
    setPhoneNumber(prev => prev.slice(0, -1));
  };

  const handleCall = () => {
    if (phoneNumber.trim()) {
      onMakeCall(phoneNumber.trim(), callSource);
      // Reset state
      setPhoneNumber('');
      setSearchQuery('');
      setSearchResults([]);
      onClose();
    }
  };

  const handleSelectCustomer = (customer: Customer) => {
    setPhoneNumber(customer.customer_phone);
    setActiveTab('dialpad');
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" 
      onClick={onClose}
    >
      <div 
        className="rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        style={{ 
          backgroundColor: theme.colors.bgCard,
          border: `1px solid ${theme.colors.borderColor}`
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: theme.colors.borderColor }}>
          <h3 className="text-lg font-semibold" style={{ color: theme.colors.textPrimary }}>
            Make a Call
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X size={20} style={{ color: theme.colors.textSecondary }} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Call Source Selector */}
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.textSecondary }}>
              Call From
            </label>
            <select
              value={JSON.stringify(callSource)}
              onChange={(e) => setCallSource(JSON.parse(e.target.value))}
              className="w-full px-3 py-2 rounded-md border text-sm focus:outline-none focus:ring-2"
              style={{
                backgroundColor: theme.colors.bgPage,
                borderColor: theme.colors.borderColor,
                color: theme.colors.textPrimary
              }}
            >
              <option value={JSON.stringify({ type: 'direct', name: 'Direct Line' })}>
                Direct Line
              </option>
              
              {queues && queues.length > 0 && (
                <optgroup label="Queues">
                  {queues.map(queue => (
                    <option 
                      key={queue.id} 
                      value={JSON.stringify({ type: 'queue', id: queue.id, name: queue.name })}
                    >
                      {queue.name}
                    </option>
                  ))}
                </optgroup>
              )}
              
              {outboundLists.length > 0 && (
                <optgroup label="Outbound Calling Lists">
                  {outboundLists.map(list => (
                    <option 
                      key={list.id} 
                      value={JSON.stringify({ type: 'outbound_list', id: list.id, name: list.name })}
                    >
                      {list.name} ({list.company_name})
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

          {/* Tab Selector */}
          <div className="flex gap-2 border-b" style={{ borderColor: theme.colors.borderColor }}>
            <button
              onClick={() => setActiveTab('dialpad')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'dialpad' ? 'border-b-2' : ''
              }`}
              style={{
                color: activeTab === 'dialpad' ? theme.colors.primaryColor : theme.colors.textSecondary,
                borderColor: activeTab === 'dialpad' ? theme.colors.primaryColor : 'transparent'
              }}
            >
              Dial Pad
            </button>
            <button
              onClick={() => setActiveTab('search')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'search' ? 'border-b-2' : ''
              }`}
              style={{
                color: activeTab === 'search' ? theme.colors.primaryColor : theme.colors.textSecondary,
                borderColor: activeTab === 'search' ? theme.colors.primaryColor : 'transparent'
              }}
            >
              Search Customers
            </button>
          </div>

          {/* Dial Pad Tab */}
          {activeTab === 'dialpad' && (
            <div className="space-y-4">
              {/* Phone Number Display */}
              <div className="relative">
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="Enter phone number"
                  className="w-full px-3 py-2 rounded-md border text-base text-center focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: theme.colors.bgPage,
                    borderColor: theme.colors.borderColor,
                    color: theme.colors.textPrimary
                  }}
                  autoFocus
                />
                {phoneNumber && (
                  <button
                    onClick={handleBackspace}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-sm px-2 py-1 rounded"
                    style={{ 
                      color: theme.colors.textSecondary,
                      backgroundColor: theme.colors.bgHover
                    }}
                  >
                    ←
                  </button>
                )}
              </div>

              {/* Dial Pad Grid */}
              <div className="grid grid-cols-3 gap-2">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((digit) => (
                  <button
                    key={digit}
                    onClick={() => handleDialPadClick(digit)}
                    className="h-12 rounded text-lg font-semibold transition-colors hover:opacity-80"
                    style={{
                      backgroundColor: theme.colors.bgPage,
                      color: theme.colors.textPrimary,
                      border: `1px solid ${theme.colors.borderColor}`
                    }}
                  >
                    {digit}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Customer Search Tab */}
          {activeTab === 'search' && (
            <div className="space-y-4">
              {/* Company Selector */}
              <div className="flex gap-2">
                {(['climate', 'hales', 'abraham'] as const).map((company) => (
                  <button
                    key={company}
                    onClick={() => setSelectedCompany(company)}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      selectedCompany === company ? 'text-white' : ''
                    }`}
                    style={{
                      backgroundColor: selectedCompany === company ? theme.colors.primaryColor : theme.colors.bgHover,
                      color: selectedCompany === company ? '#ffffff' : theme.colors.textSecondary
                    }}
                  >
                    {company === 'climate' ? 'Climate Control' : company === 'hales' ? 'Hales' : 'Abrahams'}
                  </button>
                ))}
              </div>

              {/* Search Input */}
              <div className="relative">
                <Search 
                  size={18} 
                  className="absolute left-3 top-1/2 -translate-y-1/2"
                  style={{ color: theme.colors.textSecondary }}
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name or address..."
                  className="w-full pl-10 pr-4 py-2 rounded-md border text-sm focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: theme.colors.bgPage,
                    borderColor: theme.colors.borderColor,
                    color: theme.colors.textPrimary
                  }}
                />
              </div>

              {/* Search Results */}
              <div className="max-h-64 overflow-y-auto space-y-2">
                {searching && (
                  <div className="text-center py-4 text-sm" style={{ color: theme.colors.textSecondary }}>
                    Searching...
                  </div>
                )}
                
                {!searching && searchResults.length === 0 && searchQuery.length >= 2 && (
                  <div className="text-center py-4 text-sm" style={{ color: theme.colors.textSecondary }}>
                    No customers found
                  </div>
                )}

                {searchResults.map((customer) => (
                  <button
                    key={customer.id}
                    onClick={() => handleSelectCustomer(customer)}
                    className="w-full text-left p-3 rounded-md hover:bg-opacity-50 transition-colors"
                    style={{
                      backgroundColor: theme.colors.bgHover,
                      border: `1px solid ${theme.colors.borderColor}`
                    }}
                  >
                    <div className="font-medium text-sm" style={{ color: theme.colors.textPrimary }}>
                      {customer.customer_name}
                    </div>
                    <div className="text-xs mt-1" style={{ color: theme.colors.textSecondary }}>
                      {customer.customer_phone}
                    </div>
                    <div className="text-xs" style={{ color: theme.colors.textTertiary }}>
                      {customer.customer_address}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Call Button */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm font-medium rounded-md transition-opacity"
              style={{
                backgroundColor: theme.colors.bgHover,
                color: theme.colors.textSecondary
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleCall}
              disabled={!phoneNumber.trim()}
              className="flex-1 px-4 py-2 text-white text-sm font-medium rounded-md transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ backgroundColor: theme.colors.primaryColor }}
            >
              <Phone size={16} />
              Call {phoneNumber.trim() && `(${callSource.name})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
